import {
  journeySchedulingInfoOptions,
  journeySlotsOptions,
  journeySlotsQueryKey,
  type SchedulingInfo,
  useCreateJourneySchedulingDeferralMutation,
  useUpdateJourneySlotsMutation,
} from '@cartwheel/api/v1'
import {
  Announcement,
  AnnouncementType,
  Button,
  FlowOutcome,
  IconType,
  LoadingView,
  Modal,
  Textarea,
  useToast,
} from '@cartwheel/components'
import {
  AppointmentSelector,
  type AppointmentSlot,
} from '@cartwheel/components/appointment-selector'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Temporal } from 'temporal-polyfill'
import { extractApiErrorDetails } from '../utils/api-error-details'

type Step = 'select' | 'confirm' | 'success' | 'noTimesWork' | 'deferred'
type Attendance = NonNullable<SchedulingInfo['intake_attendance']>

interface Props {
  journeyId: string
  clientFirstName: string
  clientLastName: string
  serviceName: string
}

const ATTENDANCE_LABELS: Record<Attendance, string> = {
  student_and_guardian: 'student and parent/guardian',
  student_only: 'student only',
  guardian_only: 'parent/guardian only',
  staff_only: 'staff only',
}

function formatAttendance(attendance: Attendance | null | undefined): string {
  if (!attendance) return ''
  return ATTENDANCE_LABELS[attendance] ?? ''
}

function formatDate(timestamp: string): string {
  const zdt = Temporal.ZonedDateTime.from(timestamp)
  return zdt.toPlainDate().toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatTimeRange(timestamp: string, durationMinutes: number): string {
  const zdt = Temporal.ZonedDateTime.from(timestamp)
  const end = zdt.add({ minutes: durationMinutes })
  const fmt = (t: Temporal.ZonedDateTime) =>
    t.toPlainTime().toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  const tz = zdt.timeZoneId
  const shortTz =
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
      .formatToParts(new Date(zdt.epochMilliseconds))
      .find((p) => p.type === 'timeZoneName')?.value ?? tz
  return `${fmt(zdt)}-${fmt(end)} ${shortTz}`
}

function formatDayOfWeek(timestamp: string): string {
  const zdt = Temporal.ZonedDateTime.from(timestamp)
  return zdt.toPlainDate().toLocaleString('en-US', { weekday: 'long' })
}

export default function InSchoolSchedulingModal({
  journeyId,
  clientFirstName,
  clientLastName,
  serviceName,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<Step>('select')
  const [selectedSlot, setSelectedSlot] = useState<AppointmentSlot | null>(null)
  const [availabilityNotes, setAvailabilityNotes] = useState('')
  const queryClient = useQueryClient()
  const toast = useToast()

  const schedulingInfo = useQuery({
    ...journeySchedulingInfoOptions({ path: { journey_id: journeyId } }),
    enabled: isOpen,
  })

  const slots = useQuery({
    ...journeySlotsOptions({
      path: { journey_id: journeyId },
      query: { time_period_filter: 'school_hours' },
    }),
    enabled: isOpen && schedulingInfo.data?.enabled === true,
  })

  const bookSlot = useUpdateJourneySlotsMutation()

  const deferScheduling = useCreateJourneySchedulingDeferralMutation()

  const handleOpen = () => {
    setIsOpen(true)
    setStep('select')
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedSlot(null)
    setAvailabilityNotes('')
    setStep('select')
    bookSlot.reset()
    deferScheduling.reset()
  }

  const getErrorDetails = (error: unknown): string =>
    extractApiErrorDetails(error) ?? 'Something went wrong. Please try again.'

  const handleBook = () => {
    if (!selectedSlot) return
    bookSlot.mutate(
      {
        path: { journey_id: journeyId },
        body: { selected_time: selectedSlot.timestamp },
      },
      {
        onSuccess: () => setStep('success'),
        onError: (error: unknown) => {
          if (getErrorDetails(error).includes('slot_no_longer_available')) {
            toast.error({
              description: 'That time slot is no longer available. Please select another.',
            })
            queryClient.invalidateQueries({
              queryKey: journeySlotsQueryKey({
                path: { journey_id: journeyId },
                query: { time_period_filter: 'school_hours' },
              }),
            })
            setSelectedSlot(null)
            setStep('select')
          }
        },
      }
    )
  }

  const handleDefer = () => {
    if (!availabilityNotes.trim()) return
    deferScheduling.mutate(
      {
        path: { journey_id: journeyId },
        body: { availability_notes: availabilityNotes },
      },
      {
        onSuccess: () => setStep('deferred'),
        onError: (error: unknown) => {
          toast.error({ description: getErrorDetails(error) })
        },
      }
    )
  }

  const info = schedulingInfo.data
  const modalSubheader = `${clientFirstName} ${clientLastName}, ${serviceName}`
  const isBiweekly = info?.is_biweekly ?? false
  const intakeDuration = info?.intake_appointment_duration_minutes ?? 60
  const ongoingDuration = info?.ongoing_appointment_duration_minutes ?? 45
  const timezone = info?.timezone ?? 'America/New_York'
  const intakeAttendance = formatAttendance(info?.intake_attendance)
  const ongoingAttendance = formatAttendance(info?.ongoing_attendance)

  const mappedSlots: AppointmentSlot[] = (slots.data?.slots ?? []).map((s) => ({
    timestamp: s.timestamp,
    recurrenceCount: s.recurrence_count,
  }))

  const recurrenceText = isBiweekly ? 'every other week' : 'every week'
  const attendanceText = intakeAttendance
    ? `${intakeAttendance.charAt(0).toUpperCase()}${intakeAttendance.slice(1)}`
    : 'Student and parent/guardian'

  const firstAppointmentCard = selectedSlot && (
    <Announcement
      type={AnnouncementType.Success}
      icon="fa-calendar"
      title="First Appointment"
      subtitle={
        <>
          <p>{formatDate(selectedSlot.timestamp)}</p>
          <p>{formatTimeRange(selectedSlot.timestamp, intakeDuration)}</p>
          {intakeAttendance && <p className="text-sm pt-2 font-medium">*{intakeAttendance}</p>}
        </>
      }
    />
  )

  const closeFooter = (
    <div className="flex justify-end pt-4">
      <Button variant="primary" onClick={handleClose}>
        Close
      </Button>
    </div>
  )

  const renderContent = (): { body: React.ReactNode; footer: React.ReactNode } => {
    if (schedulingInfo.isLoading || (schedulingInfo.data?.enabled && slots.isLoading)) {
      return { body: <LoadingView title="Loading available times..." />, footer: null }
    }

    if (schedulingInfo.error || slots.error) {
      return {
        body: (
          <FlowOutcome
            icon={IconType.Error}
            title="Something went wrong"
            description="We couldn't load scheduling information. Please try again."
          />
        ),
        footer: closeFooter,
      }
    }

    if (info && !info.enabled) {
      return {
        body: (
          <FlowOutcome
            icon={IconType.Error}
            title="Scheduling not available"
            description={
              info.disabled_reasons?.join('. ') ||
              'Scheduling is not available for this journey at this time.'
            }
          />
        ),
        footer: closeFooter,
      }
    }

    switch (step) {
      case 'select':
        return {
          body: (
            <div className="border border-gray-stroke rounded-lg p-4 space-y-4">
              <AppointmentSelector
                slots={mappedSlots}
                selectedTimestamp={selectedSlot?.timestamp}
                onSelect={setSelectedSlot}
                description={
                  <div className="space-y-2">
                    <p>
                      Choose a time that works {recurrenceText}. Future appointments will repeat on
                      this day and time.{' '}
                      <strong>{attendanceText} must join the first meeting via Zoom.</strong>
                    </p>
                    <p className="text-charcoal-50 text-sm">
                      All time slots are {intakeDuration} mins shown in{' '}
                      {timezone.replace(/_/g, ' ')}
                    </p>
                  </div>
                }
              />
              <button
                type="button"
                className="text-forest underline text-sm"
                onClick={() => setStep('noTimesWork')}
              >
                Can't find a time?
              </button>
            </div>
          ),
          footer: (
            <div className="flex justify-between pt-4">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setStep('confirm')} disabled={!selectedSlot}>
                Next
              </Button>
            </div>
          ),
        }

      case 'confirm': {
        if (!selectedSlot) return { body: null, footer: null }
        return {
          body: (
            <div className="border border-gray-stroke rounded-lg p-4 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-charcoal">Confirm new appointment</h3>
                <p className="text-charcoal-50">
                  If there are any issues with the appointment or therapist match, let us know—we're
                  here to help.
                </p>
              </div>
              <div className="space-y-4">
                {firstAppointmentCard}
                <Announcement
                  type={AnnouncementType.Important}
                  icon="fa-calendars"
                  title="Ongoing Appointments"
                  subtitle={
                    <>
                      <p>
                        {isBiweekly ? 'Every other ' : 'Every '}
                        {formatDayOfWeek(selectedSlot.timestamp)}
                      </p>
                      <p>{formatTimeRange(selectedSlot.timestamp, ongoingDuration)}</p>
                      {ongoingAttendance && (
                        <p className="text-sm pt-2 font-medium">*{ongoingAttendance}</p>
                      )}
                    </>
                  }
                />
              </div>
            </div>
          ),
          footer: (
            <div className="flex justify-between pt-4">
              <Button variant="secondary" onClick={() => setStep('select')}>
                ← Previous
              </Button>
              <Button variant="primary" onClick={handleBook} disabled={bookSlot.isPending}>
                {bookSlot.isPending ? 'Requesting...' : 'Next'}
              </Button>
            </div>
          ),
        }
      }

      case 'success': {
        if (!selectedSlot) return { body: null, footer: null }
        return {
          body: (
            <div className="border border-gray-stroke rounded-lg p-4">
              <div className="flex flex-col items-center gap-4">
                <FlowOutcome
                  icon={IconType.Success}
                  title="Appointment requested!"
                  description={`${clientFirstName}'s guardian will receive a text within 1-2 business days to confirm their first appointment.`}
                />
                <div className="w-full">{firstAppointmentCard}</div>
              </div>
            </div>
          ),
          footer: closeFooter,
        }
      }

      case 'noTimesWork':
        return {
          body: (
            <div className="border border-gray-stroke rounded-lg p-4 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-charcoal">
                  When is {clientFirstName} available to meet with us?
                </h3>
                <p className="text-charcoal-50">
                  {attendanceText} must join the first meeting via Zoom.
                </p>
              </div>
              <Textarea
                placeholder="Example: Everyday from 11-11:45am during school lunchtime"
                value={availabilityNotes}
                onChange={(e) => setAvailabilityNotes(e.target.value)}
                rows={4}
              />
            </div>
          ),
          footer: (
            <div className="flex justify-between pt-4">
              <Button variant="secondary" onClick={() => setStep('select')}>
                ← Previous
              </Button>
              <Button
                variant="primary"
                onClick={handleDefer}
                disabled={!availabilityNotes.trim() || deferScheduling.isPending}
              >
                {deferScheduling.isPending ? 'Submitting...' : 'Next'}
              </Button>
            </div>
          ),
        }

      case 'deferred':
        return {
          body: (
            <div className="border border-gray-stroke rounded-lg p-4">
              <FlowOutcome
                icon={IconType.Success}
                title="Got it - we're on it."
                description={`A team member will reach out to ${clientFirstName}'s guardian within 1-2 business days to set up their appointment.`}
              />
            </div>
          ),
          footer: closeFooter,
        }
    }
  }

  const { body, footer } = renderContent()

  return (
    <>
      <Button variant="primary" onClick={handleOpen}>
        Schedule In-School Sessions
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Schedule In-School Sessions"
        footer={footer}
      >
        <p className="text-charcoal mb-4">{modalSubheader}</p>
        {body}
      </Modal>
    </>
  )
}
